import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "jl_cart";

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Add a product item. If it already exists, bump quantity.
  const addItem = useCallback((product, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.type !== "event" && i.product_id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.type !== "event" && i.product_id === product.id
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }
      const unit = product.promo_price || product.price;
      return [
        ...prev,
        {
          type: "product",
          product_id: product.id,
          name_zh: product.name_zh,
          name_en: product.name_en,
          image: (product.images || [])[0] || null,
          unit_price: unit,
          quantity,
        },
      ];
    });
  }, []);

  // Add an event line (unique per event id — bumps quantity if re-added)
  const addEvent = useCallback((event, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.type === "event" && i.event_id === event.id);
      if (existing) {
        return prev.map((i) =>
          i.type === "event" && i.event_id === event.id
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }
      return [
        ...prev,
        {
          type: "event",
          event_id: event.id,
          name_zh: event.title_zh,
          name_en: event.title_en,
          image: event.image || null,
          unit_price: Number(event.price || 0),
          quantity,
          starts_at: event.starts_at,
        },
      ];
    });
  }, []);

  const updateQuantity = useCallback((key, quantity) => {
    setItems((prev) =>
      prev
        .map((i) => {
          const k = i.type === "event" ? `event:${i.event_id}` : `product:${i.product_id}`;
          return k === key ? { ...i, quantity } : i;
        })
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => {
      const k = i.type === "event" ? `event:${i.event_id}` : `product:${i.product_id}`;
      return k !== key;
    }));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const productItems = items.filter((i) => i.type !== "event");
  const eventItems = items.filter((i) => i.type === "event");
  const productSubtotal = productItems.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const eventsSubtotal = eventItems.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const count = items.reduce((n, i) => n + i.quantity, 0);
  const total = productSubtotal + eventsSubtotal;

  return (
    <CartContext.Provider
      value={{
        items,
        productItems,
        eventItems,
        addItem,
        addEvent,
        updateQuantity,
        removeItem,
        clearCart,
        count,
        total,
        productSubtotal,
        eventsSubtotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
